import { useState } from 'react';

import { SubscriptionPlan } from '@repo/domain/user';
import { withEventScope } from '@repo/event-processor';
import { subscriptionApi } from '@repo/subscription/api';
import CompleteIcon from '@repo/ui/components/Icon/CompleteIcon';
import { fireToast } from '@repo/ui/components/Toast';
import { buildWebsiteUrl, FEATURE_REQUEST_PLATFORM_URL } from '@repo/utils/url';
import { format } from 'date-fns';

import useFreeTrial from '../../../hooks/useFreeTrial';
import { useUserStore, userStoreSelector } from '../../../stores/userStore';

import AreYouSureContent from './AreYouSureContent/AreYouSureContent';
import { BenefitsOverview } from './BenefitsOverview/BenefitsOverview';
import { freeTrialOffboardingReasons } from './config';
import QuestionnaireContent from './QuestionnaireContent/QuestionnaireContent';
import RoadmapContent from './RoadmapContent/RoadmapContent';
import { OffboardingContentWrapper } from './styles';
import {
  OffboardingModalContentProps,
  FreeTrialOffboardingSteps,
} from './types';

const FreeTrialOffboarding: React.FC<OffboardingModalContentProps> = ({
  onSuccess,
  onStartLoading,
  onFinishLoading,
  subscription,
}) => {
  const [offboardingStep, setOffboardingStep] =
    useState<FreeTrialOffboardingSteps>(
      FreeTrialOffboardingSteps.BenefitsOverview
    );
  const [reason, setReason] = useState('');

  const { trialEndDate } = useFreeTrial();
  const user = useUserStore(userStoreSelector.user);

  if (!trialEndDate || !user) {
    return null;
  }

  const formattedTrialEndDate = format(trialEndDate, 'MMM d, yyyy');

  if (offboardingStep === FreeTrialOffboardingSteps.BenefitsOverview) {
    return (
      <OffboardingContentWrapper>
        <BenefitsOverview
          plan={user.plan || 'FREE'}
          subscription={subscription}
          trialEndDate={formattedTrialEndDate}
          onNextClick={(): void => {
            setOffboardingStep(FreeTrialOffboardingSteps.ReasonSelection);
          }}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === FreeTrialOffboardingSteps.ReasonSelection) {
    return (
      <OffboardingContentWrapper>
        <QuestionnaireContent
          header="Reason for cancellation"
          primaryButton={{
            label: 'Continue Cancellation',
            onClick: ({ reasonId, extraInput }): void => {
              setReason(`${reasonId}${extraInput ? ` (${extraInput})` : ''}`);
              setOffboardingStep(FreeTrialOffboardingSteps.AreYouSure);
            },
          }}
          reasons={freeTrialOffboardingReasons}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === FreeTrialOffboardingSteps.AreYouSure) {
    return (
      <OffboardingContentWrapper narrow>
        <AreYouSureContent
          primaryButton={{
            label: 'Cancel Trial',
            onClick: async (): Promise<void> => {
              onStartLoading();
              const response = await subscriptionApi.cancelSubscription({
                reason,
              });
              if (!response.success) {
                fireToast({
                  label: `Failed to cancel your trial. Please contact our support. ${response.error}`,
                  error: true,
                  duration: 5000,
                });
              } else {
                onSuccess?.(response.subscription);
              }
              onFinishLoading();
              setOffboardingStep(FreeTrialOffboardingSteps.Confirmation);
            },
          }}
          subheading={`These paid features will be accessible until ${formattedTrialEndDate}.`}
        />
      </OffboardingContentWrapper>
    );
  }

  if (offboardingStep === FreeTrialOffboardingSteps.Confirmation) {
    return (
      <OffboardingContentWrapper narrow>
        <RoadmapContent
          heading="Your trial has been canceled"
          icon={<CompleteIcon height="48px" />}
          primaryButton={{
            label: 'Back to Profile',
            onClick: (): void => {
              window.location.href = buildWebsiteUrl('/user/dashboard');
            },
          }}
          secondaryButton={{
            label: 'Request a Feature',
            onClick: (): void => {
              window.open(FEATURE_REQUEST_PLATFORM_URL, '_blank');
            },
          }}
          subheading={
            <>
              You will still have access to all features included in the{' '}
              {user.plan === SubscriptionPlan.EXPERT ? 'Expert' : 'Pro'} plan
              until <b>{formattedTrialEndDate}</b>. You can resubscribe anytime
              in the future.
            </>
          }
        />
      </OffboardingContentWrapper>
    );
  }

  return null;
};

export default withEventScope(FreeTrialOffboarding, {
  feature: 'freeTrialOffboarding',
});
